import {DetectionCandidate, DetectionErrorProps, DetectionNotificationProps, DetectionPatient} from "@/notification/interfaces";
import {UserSettings} from "@/settings/interfaces";
import {DetectionDriverSlug} from "@/notification/composables/useCustomerDetectionIpcEvents";
import {detectionApi} from "@/container";
import Consumer from "@/core/interfaces/consumers/Consumer";
import {isAxiosError} from "axios";

/**
 * Service class for patient detection.
 */
export default class NotificationPatientDetectionService {
    /**
     * Initialize the class.
     */
    constructor(
        private patient: DetectionPatient | null,
        private hostname: string | null,
        private settings: UserSettings,
        private driver: DetectionDriverSlug
    ) {
        //
    }

    /**
     * Check if we should proceed to detection.
     */
    async notifiablePatient(currentDetectedPatientData: DetectionPatient | null, cachedPatients: DetectionPatient[]): Promise<DetectionPatient | null> {
        if (!this.hostname || !this.patient) {
            return null;
        }

        if (this.isSamePatientData(this.patient, currentDetectedPatientData)) {
            return null;
        }

        return this.filterKnownPatient(cachedPatients);
    }

    /**
     * Fetch the patient's programs.
     *
     * @throws The HTTP error when the detection request fails.
     */
    async fetchPrograms() {
        const response = await this.handleDetection();

        if (!response?.customer) {
            return null;
        }

        return response;
    }

    /**
     * Build the error to display in the notification when the detection fails.
     */
    buildError(error: unknown): DetectionErrorProps {
        const status = isAxiosError(error)
            ? error.response?.status ?? error.code
            : undefined;

        if (status === 409) {
            const candidates: DetectionCandidate[] = (isAxiosError(error) ? error.response?.data?.candidates : null) ?? [];

            if (candidates.length > 0) {
                return {
                    title: 'Plusieurs patients',
                    message: 'Plusieurs patients correspondent à cette identité.',
                    code: status,
                    settings: this.settings,
                    candidates
                };
            }
        }

        return {
            title: 'Aucun patient',
            message: 'Pas de patient détecté',
            code: status,
            settings: this.settings
        };
    }

    /** Check if detected patient is the same as the previous one. */
    isSamePatientData(patient: DetectionPatient, currentDetectedPatientData: DetectionPatient | null): boolean {
        return !!currentDetectedPatientData
            && JSON.stringify(currentDetectedPatientData) === JSON.stringify(patient);
    }

    /**
     * Filter detected patient with the local patient cache, to avoid faulty detection calls.
     * @param cachedPatients The cached patient list.
     */
    filterKnownPatient(cachedPatients: DetectionPatient[]): DetectionPatient | null {
        if (this.patient?.nir) { // A valid patient has either a lastname and a firstname, or a nir, in which case we don't need cache validation.
            return this.patient;
        }

        if (this.patient?.softwareKey) { // The LGO's own id, read by LIVE: no cache validation needed.
            return this.patient;
        }

        if (this.patient?.key) { // A consumer key is a verified identity (emulation), never OCR text: no cache validation needed.
            return this.patient;
        }

        if (cachedPatients.length === 0) {
            return this.patient;
        }

        if (this.patient?.firstName) {
            this.patient.firstName = this.clean(this.patient.firstName);
        }

        if (this.patient?.lastName) {
            this.patient.lastName = this.clean(this.patient.lastName);
        }

        const matches = cachedPatients.filter((cachedPatient: DetectionPatient) => {
            return cachedPatient.firstName?.toUpperCase() === this.patient?.firstName?.toUpperCase() &&
                cachedPatient.lastName?.toUpperCase() === this.patient?.lastName?.toUpperCase();
        });

        if (matches.length > 1) {
            return this.patient;
        }

        this.patient = matches.length == 1 ? {...this.patient, key: matches[0].key} : null;

        return this.patient;
    }

    /**
     * Clean a name by removing double dashes (e.g. "JEAN--PIERRE" → "JEAN-PIERRE").
     */
    clean(name: string): string {
        return name.replace(/-{2,}/g, '-');
    }

    /**
     * Handle the detection of a patient.
     *
     * @throws The HTTP error when the detection request fails.
     */
    async handleDetection(): Promise<DetectionNotificationProps | null> {
        window.ipcRenderer.send('detection-start');

        try {
            const item: DetectionNotificationProps = {
                patient: this.patient!,
                customer: null,
                detectedConsumer: null,
                loyalty_cards: [],
                settings: this.settings,
                nextAppointment: undefined,
                certified: false,
                certification_status: '',
                fraud_report: undefined,
                polls: []
            };

            const detection = await detectionApi().create(this.hostname, this.patient, true, this.driver);

            if (detection?.customer) {
                window.ipcRenderer.send('customer-detected', detection.customer);
                const data = await detectionApi().read(detection.customer.id);

                const customer = data.consumers.find((consumer: Consumer) => consumer.type === 'customer');
                const beneficiaries = data.consumers.filter((consumer: Consumer) => consumer.type === 'beneficiary');

                // The consumer that actually matched the detection: prefer the key matched
                // against the local cache, fall back to the key matched by the API (set on
                // the cache bypasses: NIR present, empty cache, homonyms).
                const detectedKey = this.patient?.key ?? detection.consumer_key;
                const detectedConsumer = data.consumers.find((consumer: Consumer) => consumer.key === detectedKey)
                    ?? customer;

                // The birthday voucher is only carried by the detection
                // response: the consumers of the read endpoint don't have it.
                item.customer = {
                    ...customer,
                    beneficiaries,
                    birthday_gift_voucher: detection.customer.birthday_gift_voucher ?? null,
                };
                item.detectedConsumer = detectedConsumer ?? null;
                item.loyalty_cards = data.loyalty_cards ?? [];
                item.certified = data.certified;
                item.nextAppointment = data.next_appointment;
                item.fraud_report = detection.fraud_report;
                item.polls = data.polls;
                item.certification_status = data.certification_status;

                if (!item.patient) {
                    item.patient = {} as DetectionPatient;
                }

                // Display the detected person (possibly a beneficiary), not the insured party.
                if (detectedConsumer?.information?.first_name) {
                    item.patient.firstName = detectedConsumer.information.first_name;
                }
                if (detectedConsumer?.information?.last_name) {
                    item.patient.lastName = detectedConsumer.information.last_name;
                }
                if (detectedConsumer?.information?.birth_date) {
                    item.patient.birthDate = detectedConsumer.information.birth_date;
                }
            }

            return item;
        } finally {
            window.ipcRenderer.send('detection-completed');
        }
    };
}
